import React, { useState, useEffect, useRef } from 'react';
import ExcelJS from 'exceljs';
import reportService from '../../services/reportService';
import { supplierService } from '../../services/supplierService';
import Toast from '../../components/Common/Toast';
import { formatDateLocaleLima } from '../../utils/dateUtils';

const PurchaseOrderTraceability = () => {
  const [traceabilityData, setTraceabilityData] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  
  // Filtros
  const [purchaseOrderCode, setPurchaseOrderCode] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Ref para evitar llamadas duplicadas
  const hasLoadedSuppliers = useRef(false);

  useEffect(() => {
    if (!hasLoadedSuppliers.current) {
      hasLoadedSuppliers.current = true;
      loadSuppliers();
    }
  }, []);

  const loadSuppliers = async () => {
    try {
      const response = await supplierService.getSuppliers({ active_only: true });
      if (response.success) {
        setSuppliers(response.data || []);
      }
    } catch (error) {
      console.error('Error al cargar proveedores:', error);
    }
  };

  const loadTraceability = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (purchaseOrderCode) filters.purchase_order_code = purchaseOrderCode;
      if (selectedSupplier) filters.supplier_id = selectedSupplier;
      if (dateFrom) filters.date_from = dateFrom;
      if (dateTo) filters.date_to = dateTo;

      const response = await reportService.getPurchaseOrderTraceability(filters);
      
      if (response.success) {
        setTraceabilityData(response.data || []);
        if (response.data.length === 0) {
          setToast({
            isOpen: true,
            message: 'No se encontraron órdenes de compra con los filtros seleccionados',
            type: 'info'
          });
        }
      } else {
        setToast({
          isOpen: true,
          message: response.message || 'Error al cargar la trazabilidad',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error al cargar trazabilidad:', error);
      setToast({
        isOpen: true,
        message: 'Error al cargar la trazabilidad de órdenes de compra',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = async () => {
    if (traceabilityData.length === 0) {
      setToast({
        isOpen: true,
        message: 'No hay datos para exportar',
        type: 'warning'
      });
      return;
    }

    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Trazabilidad OC');

      // Estilos
      const titleStyle = {
        font: { bold: true, size: 16, color: { argb: 'FF1F4E78' } },
        alignment: { horizontal: 'center', vertical: 'middle' }
      };

      const headerStyle = {
        font: { bold: true, color: { argb: 'FFFFFFFF' } },
        fill: {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF1F4E78' }
        },
        alignment: { horizontal: 'center', vertical: 'middle', wrapText: true },
        border: {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        }
      };

      // Título
      worksheet.mergeCells('A1:F1');
      const titleRow = worksheet.getRow(1);
      titleRow.height = 30;
      titleRow.getCell(1).value = 'Reporte de Trazabilidad de Órdenes de Compra';
      titleRow.getCell(1).style = titleStyle;

      // Encabezados
      const headers = [
        'Código OC',
        'Proveedor',
        'Fecha Orden',
        'Total',
        'Documentos Generados',
        'Guías Generadas'
      ];

      const headerRow = worksheet.getRow(2);
      headers.forEach((header, index) => {
        const cell = headerRow.getCell(index + 1);
        cell.value = header;
        cell.style = headerStyle;
      });
      headerRow.height = 25;

      // Datos
      let rowIndex = 3;
      traceabilityData.forEach((trace) => {
        const po = trace.purchase_order;
        const documents = trace.derivations.documents.map(d => d.code).join(', ') || 'N/A';
        const guides = trace.derivations.entry_guides.map(g => g.code).join(', ') || 'N/A';

        const row = worksheet.getRow(rowIndex);
        row.getCell(1).value = po.code;
        row.getCell(2).value = po.supplier?.name || 'N/A';
        row.getCell(3).value = po.order_date ? formatDateLocaleLima(po.order_date) : 'N/A';
        row.getCell(4).value = parseFloat(po.total_amount || 0);
        row.getCell(5).value = documents;
        row.getCell(6).value = guides;

        rowIndex++;
      });

      // Ajustar anchos de columna
      worksheet.columns.forEach((column) => {
        column.width = 20;
      });

      // Generar archivo
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Trazabilidad_OC_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);

      setToast({
        isOpen: true,
        message: 'Reporte exportado exitosamente',
        type: 'success'
      });
    } catch (error) {
      console.error('Error al exportar:', error);
      setToast({
        isOpen: true,
        message: 'Error al exportar el reporte',
        type: 'error'
      });
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Trazabilidad de Órdenes de Compra</h1>
            <p className="text-gray-600 mt-1">Rastrea qué documentos y guías se generaron a partir de las OC</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={exportToExcel}
              disabled={traceabilityData.length === 0}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-md text-sm font-medium transition-colors flex items-center gap-2"
            >
              <span>📥</span>
              Exportar a Excel
            </button>
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Código de Orden de Compra
              </label>
              <input
                type="text"
                value={purchaseOrderCode}
                onChange={(e) => setPurchaseOrderCode(e.target.value)}
                placeholder="Ej: OC-0001"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Proveedor
              </label>
              <select
                value={selectedSupplier}
                onChange={(e) => setSelectedSupplier(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="">Todos</option>
                {suppliers.map((supplier) => (
                  <option key={supplier.id} value={supplier.id}>
                    {supplier.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fecha Desde
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fecha Hasta
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>
          <div className="mt-4">
            <button
              onClick={loadTraceability}
              disabled={loading}
              className="px-6 py-2 bg-teal-600 hover:bg-teal-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-md text-sm font-medium transition-colors"
            >
              {loading ? 'Cargando...' : 'Buscar'}
            </button>
          </div>
        </div>

        {/* Resultados */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
            <p className="mt-2 text-gray-600">Cargando trazabilidad...</p>
          </div>
        ) : traceabilityData.length > 0 ? (
          <div className="space-y-6">
            {traceabilityData.map((trace) => {
              const po = trace.purchase_order;
              return (
                <div key={po.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  {/* Información de la OC */}
                  <div className="mb-4 pb-4 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-800">{po.code}</h3>
                        <p className="text-sm text-gray-600">
                          Proveedor: {po.supplier?.name || 'N/A'} | 
                          Fecha: {po.order_date ? formatDateLocaleLima(po.order_date) : 'N/A'} | 
                          Total: S/ {parseFloat(po.total_amount || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        po.status === 'issued' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {po.status === 'issued' ? 'Emitida' : 'Cancelada'}
                      </span>
                    </div>
                  </div>

                  {/* Documentos Generados */}
                  {trace.derivations.documents.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Documentos Generados ({trace.derivations.documents.length}):</h4>
                      <div className="flex flex-wrap gap-2">
                        {trace.derivations.documents.map((doc) => (
                          <div key={doc.id} className="text-sm bg-blue-50 px-3 py-1 rounded">
                            {doc.code} {doc.series && doc.number ? `(${doc.series}-${doc.number})` : ''} - {doc.supplier?.name || 'N/A'} - {doc.issue_date ? formatDateLocaleLima(doc.issue_date) : 'N/A'} - S/ {parseFloat(doc.total_amount || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Guías Generadas */}
                  {trace.derivations.entry_guides.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Guías Generadas ({trace.derivations.entry_guides.length}):</h4>
                      <div className="flex flex-wrap gap-2">
                        {trace.derivations.entry_guides.map((guide) => (
                          <div key={guide.id} className="text-sm bg-teal-50 px-3 py-1 rounded">
                            {guide.code} ({guide.guide_type}) - {guide.supplier?.name || 'N/A'} - {guide.warehouse?.name || 'N/A'}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Items */}
                  {trace.items.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Items ({trace.items.length}):</h4>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">Producto/Servicio</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">Proyecto</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-700">Cantidad</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-700">Precio Unit.</th>
                              <th className="px-3 py-2 text-right text-xs font-medium text-gray-700">Total</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {trace.items.map((item) => (
                              <tr key={item.id}>
                                <td className="px-3 py-2 text-gray-600">
                                  {item.product ? `${item.product.sku} - ${item.product.name}` : '-'}
                                </td>
                                <td className="px-3 py-2 text-gray-600">
                                  {item.project ? `${item.project.code} - ${item.project.name}` : '-'}
                                </td>
                                <td className="px-3 py-2 text-right text-gray-900">{parseFloat(item.quantity || 0).toFixed(3)}</td>
                                <td className="px-3 py-2 text-right text-gray-900">
                                  S/ {parseFloat(item.unit_price || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                                <td className="px-3 py-2 text-right text-gray-900 font-medium">
                                  S/ {parseFloat(item.total || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
            <p className="text-gray-500">No hay datos para mostrar. Usa los filtros para buscar órdenes de compra.</p>
          </div>
        )}
      </div>

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default PurchaseOrderTraceability;
